import { useCallback } from 'react';
import { FormShowTemplate } from 'framework/form';
import { useShowContext } from 'framework/show';
import { HEALTHCHECK_NAMES, useProjectDataContext, ZONE_KEYS } from 'domain/shared/newProject';
import useEngineeringService from '../useEngineeringService';
import { ProductionOwnership, ProjectEngineeringGraphqlData } from '../types';
import EngineeringConfiguration from './EngineeringConfiguration';
import { configurationValidation } from './configurationValidation';
import useReconcileProjectNotifications, {
  PROJECT_NOTIFICATION_SCOPE,
} from '../../shared/notifications/useReconcileProjectNotifications';

const ConfigurationLayout = () => {
  const { data: showData, setData } = useShowContext<ProjectEngineeringGraphqlData>();
  const { data: projectData } = useProjectDataContext();
  const { saveConfiguration } = useEngineeringService();
  const reconcileNotifications = useReconcileProjectNotifications();

  const projectId = projectData?.id?.toString() ?? '';
  const engineeringData = showData.getData();
  const getData = useCallback(async () => engineeringData, [engineeringData]);

  return (
    <FormShowTemplate
      submitFormFunc={async formData => {
        const { productionOwnership, deliveryModel, ppcLink } = formData.engineeringPolicy;
        const updatedPolicy = await saveConfiguration(projectId, { productionOwnership, deliveryModel, ppcLink });
        await reconcileNotifications(projectId, [PROJECT_NOTIFICATION_SCOPE.ENGINEERING]).catch(() => undefined);

        const nextEngineeringPolicy = { ...engineeringData?.engineeringPolicy, ...updatedPolicy };

        const nextHealth =
          updatedPolicy.ppcLink && engineeringData?.health
            ? {
                ...engineeringData.health,
                healthChecks: (engineeringData.health.healthChecks ?? []).map(check =>
                  check.name === HEALTHCHECK_NAMES.ENGINEERING_LINK ? { ...check, targetZone: ZONE_KEYS.SAFE } : check,
                ),
              }
            : engineeringData?.health;

        const nextEngineering = { ...engineeringData, engineeringPolicy: nextEngineeringPolicy, health: nextHealth };

        setData?.(nextEngineering);
        return nextEngineering;
      }}
      afterSubmit={{ message: 'Engineering configuration has been successfully updated.' }}
      getDataFunc={getData}
      mapDataForForm={data => ({
        ...data,
        engineeringPolicy: {
          ...(data?.engineeringPolicy || {}),
          productionOwnership: data?.engineeringPolicy?.productionOwnership ?? ProductionOwnership.OUR_TEAM,
          deliveryModel: data?.engineeringPolicy?.deliveryModel ?? null,
          ppcLink: data?.engineeringPolicy?.ppcLink ?? '',
        },
      })}
      getValidationSchemaFunc={() => Promise.resolve(configurationValidation)}
    >
      <EngineeringConfiguration />
    </FormShowTemplate>
  );
};

export default ConfigurationLayout;
